import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SpofRegisterImport } from "@shared/types/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { previewSpofRegister, uploadSpofRegister } from "../api";

const ROWS_PER_PAGE = 25;

interface Props {
  plantId: string;
  file: File;
  onClose: () => void;
  onImported: (result: SpofRegisterImport) => void;
}

/** Reviews the KKS proposed for every SPOF of a workbook, and imports only once approved. */
export default function SpofRegisterImportDialog({
  plantId,
  file,
  onClose,
  onImported,
}: Props) {
  const queryClient = useQueryClient();
  const [links, setLinks] = useState<Record<string, string[]>>({});
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const { data, isPending, isError } = useQuery({
    queryKey: [
      "spof-register",
      "preview",
      plantId,
      file.name,
      file.lastModified,
    ],
    queryFn: () => previewSpofRegister(plantId, file),
    retry: false,
    staleTime: Infinity,
    // The preview is dropped with the dialog, so re-uploading the file matches the plant as it is then.
    gcTime: 0,
  });

  useEffect(() => {
    if (data) {
      setLinks(
        Object.fromEntries(data.spofs.map((spof) => [spof.spof_id, spof.kks])),
      );
    }
  }, [data]);

  const approved = Object.fromEntries(
    Object.entries(links).filter(([, kks]) => kks.length > 0),
  );

  const importMut = useMutation({
    mutationFn: () => uploadSpofRegister(plantId, file, approved),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["powerPlants"] });
      onImported(result);
    },
  });

  const spofs = useMemo(() => data?.spofs ?? [], [data]);
  // Filtering on the proposed KKS, not on the edited ones, keeps a row in place while it is edited.
  const rows = useMemo(() => {
    const term = search.trim().toUpperCase();
    return spofs.filter(
      (spof) =>
        (!onlyUnlinked || spof.kks.length === 0) &&
        (!term ||
          spof.spof_id.toUpperCase().includes(term) ||
          spof.functional_location.toUpperCase().includes(term)),
    );
  }, [spofs, onlyUnlinked, search]);

  const approvedCount = Object.keys(approved).length;
  const fuzzyCount = spofs.filter((spof) => spof.fuzzy).length;

  return (
    <Dialog
      open
      onClose={importMut.isPending ? undefined : onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Review SPOF Register Import</DialogTitle>
      <DialogContent dividers>
        {isPending && <LinearProgress />}
        {isError && (
          <Alert severity="error">
            The workbook could not be read. Nothing was imported.
          </Alert>
        )}
        {data && (
          <>
            <Alert severity={approvedCount < spofs.length ? "warning" : "info"}>
              {approvedCount} of {spofs.length} SPOF
              {spofs.length === 1 ? "" : "s"} will be imported. Review the KKS
              linked to each functional location and correct them if needed; a
              SPOF left without any KKS is skipped.
              {fuzzyCount > 0 &&
                ` ${fuzzyCount} match${fuzzyCount === 1 ? " is" : "es are"} approximate and should be checked.`}
            </Alert>
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "center", mt: 2 }}
            >
              <TextField
                size="small"
                placeholder="Search SPOF ID or functional location…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 340 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={onlyUnlinked}
                    onChange={(e) => {
                      setOnlyUnlinked(e.target.checked);
                      setPage(0);
                    }}
                  />
                }
                label="Only SPOFs without a proposed KKS"
                slotProps={{ typography: { variant: "body2" } }}
              />
            </Stack>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>SPOF</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Functional Location
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "45%" }}>
                    Linked KKS
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows
                  .slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
                  .map((spof) => (
                    <TableRow key={spof.spof_id} hover>
                      <TableCell sx={{ verticalAlign: "top", pt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {spof.spof_id}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#5a6a9a" }}>
                          {[
                            spof.equipment,
                            `${spof.mitigation_count} mitigation${spof.mitigation_count === 1 ? "" : "s"}`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top", pt: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {spof.functional_location}
                          </Typography>
                          {spof.fuzzy && (
                            <Tooltip title="Approximate match: the KKS tag was truncated to resolve this location.">
                              <WarningAmberIcon
                                sx={{ fontSize: 16, color: "#FF9800" }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          multiple
                          size="small"
                          options={data.kks_options}
                          value={links[spof.spof_id] ?? []}
                          onChange={(_, value) =>
                            setLinks((current) => ({
                              ...current,
                              [spof.spof_id]: value,
                            }))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                (links[spof.spof_id] ?? []).length
                                  ? ""
                                  : "No KKS - skipped"
                              }
                            />
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={importMut.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={approvedCount === 0 || importMut.isPending}
          onClick={() => importMut.mutate()}
        >
          Import {approvedCount} SPOF{approvedCount === 1 ? "" : "s"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
